import React, {  useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import StudentLayout from "../../layouts/StudentLayout";
import Toast from "../../components/common/Toast";
import { Arrow } from "../../components/Icons";
import { useGetPublicCourseByIdQuery, useCreateEnrollmentMutation } from "../../services/course";

const ACCEPTED_PROOF_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const MAX_PROOF_SIZE = 5 * 1024 * 1024;



const formatMoney = (value) => `Rs${Number(value || 0).toFixed(2)}`;



const LevelBars = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.75 10.2916H5.91625V15.5416H4.75V10.2916ZM14.08 7.95829H15.2463V15.5416H14.08V7.95829ZM9.415 4.45829H10.5813V15.5416H9.415V4.45829Z"
      fill="#9333EA"
    />
  </svg>
);

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    courseId,
    preferredClassTime: statePreferredClassTime,
  } = location.state || {};

  const { data: courseResponse } = useGetPublicCourseByIdQuery(courseId, {
    skip: !courseId,
  });
  const [createEnrollment, { isLoading: isCreatingEnrollment }] =
    useCreateEnrollmentMutation();
  const course = courseResponse?.data?.course;

  const title = course?.courseTitle ||  "Course";
  const image =
    course?.courseThumbnail?.secureUrl || "";
  const level =
    course?.courseLevel ||  "Intermediate";

  const enrollmentType =  course?.courseEnrollementType === "live" ? "Live Classes" : "Recorded Lectures";
  
  const preferredClassTime =
    typeof statePreferredClassTime === "string"
      ? statePreferredClassTime.trim()
      : "";

  const finalPrice = course?.coursePrice || 0;

  const [requestInvoice, setRequestInvoice] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState({
    companyName: "",
    taxId: "",
    invoiceEmail: "",
    billingAddress: "",
  });
  const [paymentProof, setPaymentProof] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    invoiceNumber: "",
    paymentProof: "",
  });
  const [isDraggingProof, setIsDraggingProof] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
    variant: "error",
  });
  const proofInputRef = useRef(null);
  const invoiceNumberRef = useRef(null);
  const paymentProofBoxRef = useRef(null);

  const scrollToField = (element) => {
    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    if (typeof element.focus === "function") {
      element.focus({ preventScroll: true });
    }
  };

  const applyProofFile = (file) => {
    if (!file) return;
    if (!ACCEPTED_PROOF_TYPES.includes(file.type)) {
      setFieldErrors((current) => ({
        ...current,
        paymentProof: "Supported formats: JPG, PNG, PDF (Max 5MB)",
      }));
      setTimeout(() => scrollToField(paymentProofBoxRef.current), 0);
      return;
    }
    if (file.size > MAX_PROOF_SIZE) {
      setFieldErrors((current) => ({
        ...current,
        paymentProof: "File size must be less than 5MB",
      }));
      setTimeout(() => scrollToField(paymentProofBoxRef.current), 0);
      return;
    }
    setPaymentProof(file);
    setFieldErrors((current) => ({ ...current, paymentProof: "" }));
  };

  const handleProofChange = (event) => {
    applyProofFile(event.target.files?.[0]);
  };

  const handleRemoveProof = () => {
    setPaymentProof(null);
    if (proofInputRef.current) {
      proofInputRef.current.value = "";
    }
  };

  const handleInvoiceNumberChange = (event) => {
    setInvoiceNumber(event.target.value);
    if (fieldErrors.invoiceNumber) {
      setFieldErrors((current) => ({ ...current, invoiceNumber: "" }));
    }
  };

  const handleProofDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingProof(true);
  };

  const handleProofDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingProof(false);
  };

  const handleProofDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingProof(false);
    applyProofFile(event.dataTransfer.files?.[0]);
  };

  const handleCancel = () => navigate('/student/courses');

  const handleInvoiceField = (field) => (event) => {
    setInvoiceDetails((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleSubmitPayment = async () => {
    const trimmedInvoiceNumber = invoiceNumber.trim();
    const nextErrors = {
      invoiceNumber: "",
      paymentProof: "",
    };

    if (!paymentProof) {
      nextErrors.paymentProof = "Please upload a payment screenshot";
    }

    if (!trimmedInvoiceNumber) {
      nextErrors.invoiceNumber =
        "Please enter your invoice / transaction number";
    }

    if (nextErrors.paymentProof || nextErrors.invoiceNumber) {
      setFieldErrors(nextErrors);

      setTimeout(() => {
        if (nextErrors.invoiceNumber) {
          scrollToField(invoiceNumberRef.current);
          return;
        }

        if (nextErrors.paymentProof) {
          scrollToField(paymentProofBoxRef.current);
        }
      }, 0);

      return;
    }

    if (!courseId) {
      setToast({
        show: true,
        title: "Course required",
        message: "Course information is missing. Please go back and try again.",
        variant: "error",
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("paymentScreenshot", paymentProof);
      formData.append("enrollmentType", enrollmentType);
      formData.append("invoiceNumber", trimmedInvoiceNumber);

      if (preferredClassTime) {
        formData.append("preferredClassTime", preferredClassTime);
      }

      const response = await createEnrollment({ courseId, formData }).unwrap();

      if (response?.success === false) {
        setToast({
          show: true,
          title: "Enrollment failed",
          message:
            response?.message ||
            "Unable to complete enrollment. Please try again.",
          variant: "error",
        });
        return;
      }

      navigate("/student", { replace: true });
    } catch (error) {
      setToast({
        show: true,
        title: "Enrollment failed",
        message:
          error?.data?.message ||
          error?.message ||
          "Unable to complete enrollment. Please try again.",
        variant: "error",
      });
    }
  };

  return (
    <StudentLayout>
      <section className="font-['Roboto']">
        <div className="py-9 bg-[#F5F5F5] px-6 lg:px-10 border-b border-[#938A96]">
          <div className="max-w-[1528px] mx-auto flex items-center gap-16 ">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center justify-center text-black"
              aria-label="Go back"
            >
              <Arrow />
            </button>
            <h1 className="text-[40px] leading-8 font-semibold tracking-[-1%] text-[#1D2026]">
              Checkout
            </h1>
          </div>
        </div>
       
       <div className="px-6">
        <div className="pt-16 pb-20  grid w-full max-w-[1594px] mx-auto grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1152px)_minmax(280px,418px)]">
          <div className="w-full space-y-6">
            <div className="rounded-lg border border-[#E5E7EB] bg-white p-5 shadow-[0_1px_2px_0_#0000000D] sm:p-6">
              <h2 className="text-xl font-bold text-[#1F2937]">
                Course Summary
              </h2>

              <div className="mt-6 flex flex-col gap-[18px] sm:flex-row items-center">
                <img
                  src={image}
                  alt={title}
                  className="h-36 w-48 shrink-0 rounded-lg object-cover"
                />
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-semibold text-[#1F2937]">
                    {title}
                  </h3>
                  {/* {purchaseType ? (
                    <p className="mt-2 text-sm text-[#6B7280]">
                      {purchaseType}
                    </p>
                  ) : null} */}
                  <div className="mt-2 flex items-center gap-2">
                    <LevelBars />
                    <span className="text-[#4B5563] text-sm">
                      Level: <span className="font-medium">{level}</span>
                    </span>
                  </div>
                  <p className="mt-[18px] text-2xl font-bold text-[#F97316] ">
                    {formatMoney(finalPrice)}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-[#E5E7EB] bg-white p-5 shadow-[0_1px_2px_0_#0000000D] sm:p-6">
              <h2 className="text-xl font-bold text-[#1F2937]">
                Payment Instructions
              </h2>

              <div className="flex gap-3 mt-6 rounded-lg p-5 sm:p-6 border border-[#F3E8FF] bg-gradient-to-r from-[#FAF5FF] to-[#FFF7ED]">
                <div className=" flex h-10 w-10 items-center justify-center rounded-lg bg-[#9333EA] ">
                  <svg
                    width="21"
                    height="20"
                    viewBox="0 0 21 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.08203 16.6667H18.7445V18.3334H2.08203V16.6667ZM3.74828 10H5.41453V15.8334H3.74828V10ZM7.91391 10H9.58016V15.8334H7.91391V10ZM11.2464 10H12.9127V15.8334H11.2464V10ZM15.412 10H17.0783V15.8334H15.412V10ZM2.08203 5.83337L10.4133 1.66671L18.7445 5.83337V9.16671H2.08203V5.83337ZM3.74828 6.86671V7.50004H17.0783V6.86671L10.4133 3.53337L3.74828 6.86671ZM10.4133 6.66671C10.18 6.66671 9.98283 6.58615 9.82176 6.42504C9.66069 6.26393 9.58016 6.06671 9.58016 5.83337C9.58016 5.60004 9.66069 5.40282 9.82176 5.24171C9.98283 5.0806 10.18 5.00004 10.4133 5.00004C10.6466 5.00004 10.8437 5.0806 11.0048 5.24171C11.1659 5.40282 11.2464 5.60004 11.2464 5.83337C11.2464 6.06671 11.1659 6.26393 11.0048 6.42504C10.8437 6.58615 10.6466 6.66671 10.4133 6.66671Z"
                      fill="white"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-[#1F2937]">
                    Bank Transfer
                  </h3>
                  <div className="mt-2 space-y-1 text-sm  text-[#4B5563]">
                    <p>
                      <span className="font-bold">Bank Name:</span> Allied Bank
                      Limited
                    </p>
                    <p>
                      <span className="font-bold">Account Title:</span> EduLearn
                      Platform
                    </p>
                    <p>
                      <span className="font-bold">Account Number:</span>{" "}
                      0123-4567-8901-2345
                    </p>
                    <p>
                      <span className="font-bold">IBAN:</span>{" "}
                      PK12ABCD0123456789012345
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-8">
                <div className="rounded-lg border border-[#BBF7D0] bg-gradient-to-r from-[#F0FDF4] to-[#DCFCE7]  p-4 sm:p-5">
                  <div className="flex items-center gap-3">
                    <div className=" flex h-10 w-10 items-center justify-center rounded-lg bg-[#16A34A]">
                      <svg
                        width="21"
                        height="20"
                        viewBox="0 0 21 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.24828 3.33337V16.6667H14.5795V3.33337H6.24828ZM5.41516 1.66671H15.4127C15.6459 1.66671 15.8431 1.74726 16.0042 1.90837C16.1652 2.06949 16.2458 2.26671 16.2458 2.50004V17.5C16.2458 17.7334 16.1652 17.9306 16.0042 18.0917C15.8431 18.2528 15.6459 18.3334 15.4127 18.3334H5.41516C5.18188 18.3334 4.98471 18.2528 4.82364 18.0917C4.66257 17.9306 4.58203 17.7334 4.58203 17.5V2.50004C4.58203 2.26671 4.66257 2.06949 4.82364 1.90837C4.98471 1.74726 5.18188 1.66671 5.41516 1.66671ZM10.4139 14.1667C10.6472 14.1667 10.8444 14.2473 11.0054 14.4084C11.1665 14.5695 11.247 14.7667 11.247 15C11.247 15.2334 11.1665 15.4306 11.0054 15.5917C10.8444 15.7528 10.6472 15.8334 10.4139 15.8334C10.1806 15.8334 9.98346 15.7528 9.82239 15.5917C9.66132 15.4306 9.58078 15.2334 9.58078 15C9.58078 14.7667 9.66132 14.5695 9.82239 14.4084C9.98346 14.2473 10.1806 14.1667 10.4139 14.1667Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-[#1F2937]">Easypaisa</h3>
                  </div>
                  <div className="mt-3 space-y-1 text-sm  text-[#4B5563]">
                    <p>
                      <span className="font-bold">Account:</span> 0300-1234567
                    </p>
                    <p>
                      <span className="font-bold">Name:</span> EduLearn
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-[#FECACA] bg-gradient-to-r from-[#FEF2F2] to-[#FEE2E2]  p-4 sm:p-5">
                  <div className="flex items-center gap-3">
                    <div className=" flex h-10 w-10 items-center justify-center rounded-lg bg-[#DC2626]">
                      <svg
                        width="21"
                        height="20"
                        viewBox="0 0 21 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.24828 3.33337V16.6667H14.5795V3.33337H6.24828ZM5.41516 1.66671H15.4127C15.6459 1.66671 15.8431 1.74726 16.0042 1.90837C16.1652 2.06949 16.2458 2.26671 16.2458 2.50004V17.5C16.2458 17.7334 16.1652 17.9306 16.0042 18.0917C15.8431 18.2528 15.6459 18.3334 15.4127 18.3334H5.41516C5.18188 18.3334 4.98471 18.2528 4.82364 18.0917C4.66257 17.9306 4.58203 17.7334 4.58203 17.5V2.50004C4.58203 2.26671 4.66257 2.06949 4.82364 1.90837C4.98471 1.74726 5.18188 1.66671 5.41516 1.66671ZM10.4139 14.1667C10.6472 14.1667 10.8444 14.2473 11.0054 14.4084C11.1665 14.5695 11.247 14.7667 11.247 15C11.247 15.2334 11.1665 15.4306 11.0054 15.5917C10.8444 15.7528 10.6472 15.8334 10.4139 15.8334C10.1806 15.8334 9.98346 15.7528 9.82239 15.5917C9.66132 15.4306 9.58078 15.2334 9.58078 15C9.58078 14.7667 9.66132 14.5695 9.82239 14.4084C9.98346 14.2473 10.1806 14.1667 10.4139 14.1667Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    <h3 className="font-semibold text-[#1F2937]">JazzCash</h3>
                  </div>
                  <div className="mt-3 space-y-1 text-sm  text-[#4B5563]">
                    <p>
                      <span className="font-bold">Account:</span>{" "}
                      0321-9876543-173
                    </p>
                    <p>
                      <span className="font-bold">Name:</span> EduLearn
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-lg bg-[#EFF6FF] border-[#BFDBFE]  p-4 sm:p-5">
                <div className="flex  gap-3">
                  <svg
                    width="21"
                    height="20"
                    viewBox="0 0 21 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.4133 18.3333C9.28023 18.3333 8.19717 18.1166 7.16409 17.6833C6.17545 17.2611 5.29512 16.6639 4.52309 15.8916C3.75106 15.1194 3.15399 14.2389 2.73187 13.25C2.29864 12.2166 2.08203 11.1333 2.08203 9.99998C2.08203 8.86665 2.29864 7.78331 2.73187 6.74998C3.15399 5.76109 3.75106 4.88054 4.52309 4.10831C5.29512 3.33609 6.17545 2.73887 7.16409 2.31665C8.19717 1.88331 9.28023 1.66665 10.4133 1.66665C11.5463 1.66665 12.6294 1.88331 13.6625 2.31665C14.6511 2.73887 15.5314 3.33609 16.3035 4.10831C17.0755 4.88054 17.6726 5.76109 18.0947 6.74998C18.5279 7.78331 18.7445 8.86665 18.7445 9.99998C18.7445 11.1333 18.5279 12.2166 18.0947 13.25C17.6726 14.2389 17.0755 15.1194 16.3035 15.8916C15.5314 16.6639 14.6511 17.2611 13.6625 17.6833C12.6294 18.1166 11.5463 18.3333 10.4133 18.3333ZM10.4133 16.6666C11.6241 16.6666 12.746 16.3611 13.7791 15.75C14.7789 15.1611 15.5731 14.3666 16.1618 13.3666C16.7728 12.3333 17.0783 11.2111 17.0783 9.99998C17.0783 8.78887 16.7728 7.66665 16.1618 6.63331C15.5731 5.63331 14.7789 4.83887 13.7791 4.24998C12.746 3.63887 11.6241 3.33331 10.4133 3.33331C9.20247 3.33331 8.08053 3.63887 7.04746 4.24998C6.04771 4.83887 5.25346 5.63331 4.66472 6.63331C4.05376 7.66665 3.74828 8.78887 3.74828 9.99998C3.74828 11.2111 4.05376 12.3333 4.66472 13.3666C5.25346 14.3666 6.04771 15.1611 7.04746 15.75C8.08053 16.3611 9.20247 16.6666 10.4133 16.6666ZM9.58016 5.83331H11.2464V7.49998H9.58016V5.83331ZM9.58016 9.16665H11.2464V14.1666H9.58016V9.16665Z"
                      fill="#2563EB"
                    />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-[#1E40AF]">
                      Important Instructions
                    </h4>
                    <ul className="mt-3 list-disc space-y-1 pl-3  text-sm  text-[#1D4ED8]">
                      <li>
                        Transfer the exact amount:{" "}
                        <span className="font-semibold">
                          {formatMoney(finalPrice)}
                        </span>
                      </li>
                      <li>
                        Take a clear screenshot of the payment confirmation
                      </li>
                      <li>Upload the screenshot below</li>
                      <li>
                        Your enrollment will be activated within 24 hours after
                        verification
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-[#E5E7EB] bg-white p-5 shadow-[0_1px_2px_0_#0000000D] sm:p-6">
              <h2 className="text-xl font-bold text-[#1F2937]">
                Upload Payment Screenshot
              </h2>

              <div className="mt-6">
                <label
                  htmlFor="invoiceNumber"
                  className="mb-2 block text-sm font-medium text-[#1F2937]"
                >
                  Invoice / Transaction Number
                </label>
                <input
                  ref={invoiceNumberRef}
                  id="invoiceNumber"
                  type="text"
                  value={invoiceNumber}
                  onChange={handleInvoiceNumberChange}
                  placeholder="Enter invoice or transaction ID"
                  className={`h-11 w-full rounded-lg border bg-white px-3 text-sm text-[#1F2937] outline-none placeholder:text-[#9CA3AF] focus:border-[#9333EA] ${
                    fieldErrors.invoiceNumber
                      ? "border-[#EF4444]"
                      : "border-[#D1D5DB]"
                  }`}
                />
                {fieldErrors.invoiceNumber ? (
                  <p className="mt-2 text-sm font-medium text-[#EF4444]">
                    {fieldErrors.invoiceNumber}
                  </p>
                ) : null}
              </div>

              <input
                ref={proofInputRef}
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                className="hidden"
                onChange={handleProofChange}
              />
              <div
                ref={paymentProofBoxRef}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!paymentProof) proofInputRef.current?.click();
                }}
                onKeyDown={(event) => {
                  if (!paymentProof && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    proofInputRef.current?.click();
                  }
                }}
                onDragOver={handleProofDragOver}
                onDragLeave={handleProofDragLeave}
                onDrop={handleProofDrop}
                className={`mt-6 flex min-h-[320px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors ${
                  fieldErrors.paymentProof
                    ? "border-[#EF4444] bg-[#FEF2F2]"
                    : isDraggingProof
                      ? "border-[#9333EA] bg-[#FCFAFF]"
                      : "border-[#D1D5DB] bg-white hover:border-[#9333EA] hover:bg-[#FCFAFF]"
                } ${paymentProof ? "cursor-default" : "cursor-pointer"}`}
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F3E8FF]">
                  <svg
                    width="32"
                    height="30"
                    viewBox="0 0 32 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M1.875 18.75C1.875 17.3167 2.225 15.9833 2.925 14.75C3.60833 13.55 4.53333 12.5833 5.7 11.85C5.91667 10.2333 6.4875 8.75833 7.4125 7.425C8.3375 6.09167 9.50833 5.05 10.925 4.3C12.3917 3.51667 13.9583 3.125 15.625 3.125C17.2917 3.125 18.8583 3.51667 20.325 4.3C21.7417 5.05 22.9125 6.09167 23.8375 7.425C24.7625 8.75833 25.3333 10.2333 25.55 11.85C26.7167 12.5833 27.6417 13.55 28.325 14.75C29.025 15.9833 29.375 17.3083 29.375 18.725C29.375 20.1417 29.0417 21.4583 28.375 22.675C27.7083 23.8583 26.8042 24.825 25.6625 25.575C24.5208 26.325 23.2583 26.75 21.875 26.85L9.375 26.875C7.99167 26.7583 6.72917 26.325 5.5875 25.575C4.44583 24.825 3.54167 23.8583 2.875 22.675C2.20833 21.4583 1.875 20.15 1.875 18.75ZM21.675 24.35C22.6417 24.2833 23.5208 23.9917 24.3125 23.475C25.1042 22.9583 25.7292 22.2833 26.1875 21.45C26.6458 20.6167 26.875 19.7125 26.875 18.7375C26.875 17.7625 26.6375 16.8542 26.1625 16.0125C25.6875 15.1708 25.0417 14.4917 24.225 13.975L23.225 13.35L23.075 12.175C22.9083 10.9417 22.475 9.82917 21.775 8.8375C21.075 7.84583 20.1875 7.0625 19.1125 6.4875C18.0375 5.9125 16.875 5.625 15.625 5.625C14.375 5.625 13.2125 5.9125 12.1375 6.4875C11.0625 7.0625 10.175 7.84583 9.475 8.8375C8.775 9.82917 8.34167 10.9417 8.175 12.175L8.025 13.35L7.025 13.975C6.20833 14.4917 5.5625 15.1708 5.0875 16.0125C4.6125 16.8542 4.375 17.7625 4.375 18.7375C4.375 19.7125 4.60417 20.6167 5.0625 21.45C5.52083 22.2833 6.14583 22.9583 6.9375 23.475C7.72917 23.9917 8.60833 24.2833 9.575 24.35L9.775 24.375H21.475L21.675 24.35ZM16.875 16.875V21.875H14.375V16.875H10.625L15.625 10.625L20.625 16.875H16.875Z"
                      fill="#9333EA"
                    />
                  </svg>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-[#1F2937]">
                  Upload Payment Proof
                </h3>
                <p className="mt-2 text-sm text-[#4B5563]">
                  Drag and drop your screenshot here, or click to browse
                </p>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();
                    proofInputRef.current?.click();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                      proofInputRef.current?.click();
                    }
                  }}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#9333EA] px-6 py-2.5 text-sm font-medium text-white "
                >
                  <svg
                    width="15"
                    height="14"
                    viewBox="0 0 15 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M1.89947 12.25C1.7362 12.25 1.59625 12.1936 1.47963 12.0808C1.363 11.9681 1.30469 11.83 1.30469 11.6667V2.33333C1.30469 2.17 1.363 2.03194 1.47963 1.91917C1.59625 1.80639 1.7362 1.75 1.89947 1.75H6.2146L7.38085 2.91667H11.8126C11.9681 2.91667 12.1022 2.97306 12.215 3.08583C12.3277 3.19861 12.3841 3.33667 12.3841 3.5V5.25H11.2295V4.08333H6.90269L5.73644 2.91667H2.4826V9.91667L3.35729 6.41667H13.2704L11.9176 11.8067C11.8865 11.9389 11.8184 12.0458 11.7135 12.1275C11.6085 12.2092 11.486 12.25 11.3461 12.25H1.89947ZM11.766 7.58333H4.26696L3.39228 11.0833H10.8913L11.766 7.58333Z"
                      fill="white"
                    />
                  </svg>
                  Choose File
                </span>
                <p className="mt-3 text-xs text-[#6B7280]">
                  Supported formats: JPG, PNG, PDF (Max 5MB)
                </p>
                {paymentProof ? (
                  <div
                    className="mt-4 flex w-full max-w-xl items-center justify-between gap-3 rounded-lg bg-[#F9FAFB] px-4 py-3 text-left"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <span className="min-w-0 truncate text-sm text-[#4B5563]">
                      {paymentProof.name}
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveProof}
                      className="shrink-0 text-sm font-medium text-[#EF4444] transition-colors hover:text-[#DC2626]"
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
              </div>
              {fieldErrors.paymentProof ? (
                <p className="mt-2 text-sm font-medium text-[#EF4444]">
                  {fieldErrors.paymentProof}
                </p>
              ) : null}
            </div>
          </div>

          <div className="self-start ">
            <div className="max-w-[352px]">
            <div className="rounded-lg   border border-[#E5E7EB] bg-white p-5 shadow-[0_1px_2px_0_#0000000D] sm:p-6">
              <h2 className="text-xl font-bold text-[#1F2937]">
                Order Summary
              </h2>

              <div className="mt-6 space-y-4 text-sm">
                <div className="flex items-center justify-between text-[#4B5563]">
                  <span>Course Price</span>
                  <span className="text-[#1F2937] font-medium">
                    {formatMoney(finalPrice)}
                  </span>
                </div>
                <div className="border-t border-[#E5E7EB] pt-4">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-lg text-[#1F2937]">
                      Total Amount:
                    </span>
                    <span className="text-2xl font-bold text-[#F97316]">
                      {formatMoney(finalPrice)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                disabled
                onClick={handleSubmitPayment}
                className="disabled:opacity-55 mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#9333EA] to-[#F97316] px-4 py-2 text-sm font-medium text-white  transition-opacity hover:opacity-95"
              >
                <Check size={14} />
                Submit Payment
              </button>

              <button
                type="button"
                onClick={handleCancel}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#D1D5DB] bg-white px-4 py-[9px] text-sm font-medium text-[#374151] transition-colors hover:bg-[#F9FAFB]"
              >
                <X size={14} />
                Cancel
              </button>

              <div className="mt-6 border-t border-[#E5E7EB] pt-6 flex items-center gap-3   text-xs  text-[#4B5563]">
                <svg
                  width="19"
                  height="18"
                  viewBox="0 0 19 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.375 0.75L15.54 2.115C15.71 2.155 15.85 2.245 15.96 2.385C16.07 2.525 16.125 2.68 16.125 2.85V10.335C16.125 11.095 15.9475 11.805 15.5925 12.465C15.2375 13.125 14.745 13.665 14.115 14.085L9.375 17.25L4.635 14.085C4.005 13.665 3.5125 13.125 3.1575 12.465C2.8025 11.805 2.625 11.095 2.625 10.335V2.85C2.625 2.68 2.68 2.525 2.79 2.385C2.9 2.245 3.04 2.155 3.21 2.115L9.375 0.75ZM9.375 2.28L4.125 3.45V10.335C4.125 10.845 4.2425 11.32 4.4775 11.76C4.7125 12.2 5.04 12.56 5.46 12.84L9.375 15.45L13.29 12.84C13.71 12.56 14.0375 12.2 14.2725 11.76C14.5075 11.32 14.625 10.845 14.625 10.335V3.45L9.375 2.28ZM12.72 6.165L13.77 7.23L9 12L5.82 8.82L6.885 7.755L9 9.885L12.72 6.165Z"
                    fill="#16A34A"
                  />
                </svg>

                <p>
                  Your payment information is secure. We will verify your
                  payment and activate your enrollment within 24 hours.
                </p>
              </div>
            </div>

         
            <button
              type="button"
              onClick={handleSubmitPayment}
              disabled={isCreatingEnrollment}
              className="mt-[18px] w-full bg-[#FF6636] px-4 py-[14px] text-lg font-semibold text-white transition-colors tracking-[-1%] font-['Inter'] hover:bg-[#e65a2e] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isCreatingEnrollment ? "Submitting…" : "Complete Payment"}
            </button>
            </div>
            {/* <div className="mt-12">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-xl font-bold text-[#1F2937]">
                  Invoice Information
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setRequestInvoice((current) => !current)}
                    aria-pressed={requestInvoice}
                    aria-label="Toggle request invoice"
                    className="flex h-[27px] w-[27px] shrink-0 items-center justify-center rounded-full bg-[#D9D9D9] p-[3px] transition-colors duration-200"
                  >
                    <span
                      className={`block h-full w-full rounded-full transition-colors duration-200 ${
                        requestInvoice ? "bg-[#923D8C]" : "bg-transparent"
                      }`}
                    />
                  </button>
                  <span className="text-base text-[#1F2937]">Request Invoice</span>
                </div>
              </div>

              {requestInvoice ? (
                <div className="mt-4 bg-[#F9F9F9] pt-5 pl-7 pr-4 pb-14 shadow-[0_4px_4px_0_#00000040] space-y-3">
                  <div>
                    <label
                      htmlFor="companyName"
                      className="block text-xs  leading-7 text-[#1F2937]"
                    >
                      Company Name
                    </label>
                    <input
                      id="companyName"
                      type="text"
                      value={invoiceDetails.companyName}
                      onChange={handleInvoiceField("companyName")}
                      placeholder="Your company Name"
                      className="mt-1.5 w-full rounded-[10px] border-[1.5px] border-[#F4F4F4] px-2.5  text-xs leading-7  text-[#1F2937] outline-none placeholder:text-[#6B7280] focus:border-[#9333EA]"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="taxId"
                     className="block text-xs  leading-7 text-[#1F2937]"
                    >
                      Tax ID/ VAT Number
                    </label>
                    <input
                      id="taxId"
                      type="text"
                      value={invoiceDetails.taxId}
                      onChange={handleInvoiceField("taxId")}
                      placeholder="1234567890"
                      className="mt-1.5 w-full rounded-[10px] border-[1.5px] border-[#F4F4F4] px-2.5  text-xs leading-7  text-[#1F2937] outline-none placeholder:text-[#6B7280] focus:border-[#9333EA]"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="invoiceEmail"
                      className="block text-xs  leading-7 text-[#1F2937]"
                    >
                      Invoice Email
                    </label>
                    <input
                      id="invoiceEmail"
                      type="email"
                      value={invoiceDetails.invoiceEmail}
                      onChange={handleInvoiceField("invoiceEmail")}
                      placeholder="billing@company.com"
                      className="mt-1.5 w-full rounded-[10px] border-[1.5px] border-[#F4F4F4] px-2.5  text-xs leading-7  text-[#1F2937] outline-none placeholder:text-[#6B7280] focus:border-[#9333EA]"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="billingAddress"
                       className="block text-xs  leading-7 text-[#1F2937]"
                    >
                      Billing Address for Invoice
                    </label>
                    <textarea
                      id="billingAddress"
                      value={invoiceDetails.billingAddress}
                      onChange={handleInvoiceField("billingAddress")}
                      placeholder="Company Billing Address"
                      rows={2}
                      className="mt-1.5 w-full resize-none rounded-[10px] border border-[#F4F4F4] px-2.5 py-4 text-xs leading-7 text-[#1F2937] outline-none placeholder:text-[#9CA3AF] focus:border-[#9333EA]"
                    />
                  </div>
                </div>
              ) : null}
            </div> */}
          </div>
            
         

          
        </div>
        </div>
      </section>
      {toast.show ? (
        <Toast
          variant={toast.variant}
          title={toast.title}
          message={toast.message}
          onClose={() => setToast((current) => ({ ...current, show: false }))}
        />
      ) : null}
    </StudentLayout>
  );
};

export default Checkout;
